import api from './api'
import type { G5LinkStatus } from '@/components/Financeiro/g5LinkStatus'

export type FinanceSummary = {
  openRequests: number
  overdueSla: number
  slaDueSoon: number
  projectsWithoutContract: number
  projectsByStatus: { status: string; count: number }[]
}

export type FinanceRequestRow = {
  id: string
  protocol: string
  area: string
  type: string
  status: string
  slaDueAt: string | null
  createdAt: string
  project?: FinanceRequestProjectSummary | null
  requester?: { id: string; name: string } | null
  assignee?: { id: string; name: string } | null
  clientDisplay?: {
    label: string
    kind: 'cliente' | 'assunto' | 'interno'
    hint: string | null
  }
}

export type FinanceRequestProjectSummary = {
  id: string
  name: string | null
  status: string
  source?: string
  empresa?: {
    id: string
    nome: string
    nomeFantasia?: string | null
    cnpj: string | null
    razaoSocial?: string | null
  } | null
  deal?: {
    id: string
    nome: string
    valor: number | string | null
    etapa: string
    dataFechamento: string | null
  } | null
  proposalSnapshots?: FinanceProposalSnapshotRow[]
  contracts?: FinanceContractRow[]
}

export type FinanceAssignee = { id: string; name: string; role: string }

export type FinanceRequestComment = {
  id: string
  body: string
  attachments?: string[]
  isInternal?: boolean
  createdAt: string
  author: { id: string; name: string }
}

export type FinanceG5PjLink = {
  linkStatus: G5LinkStatus
  pj: {
    id: string
    nomeCompleto: string
    email: string | null
    telefone: string
    status: string
    areaAtuacao: string | null
    cpf: string | null
    cnpj: string | null
    remuneracaoMensal: number | null
    dataInicioContrato: string | null
  } | null
  invite: {
    id: string
    usedAt: string | null
    expiresAt: string
    url: string | null
  } | null
  completeness: {
    fields: { key: string; label: string; ok: boolean }[]
    missing: { key: string; label: string; ok: boolean }[]
    complete: boolean
    percent: number
  } | null
}

export type FinanceRequestDetail = FinanceRequestRow & {
  typeLabel: string
  source: string
  slaHours: number | null
  formData: Record<string, unknown>
  attachments: string[]
  approvalPendingFrom?: string | null
  requester?: { id: string; name: string; email: string } | null
  assignee?: { id: string; name: string } | null
  comments: FinanceRequestComment[]
  g5PjLink?: FinanceG5PjLink | null
}

/** Garante arrays quando a API omitir campos opcionais em registros antigos. */
export function normalizeFinanceRequestDetail(
  detail: FinanceRequestDetail,
): FinanceRequestDetail {
  return {
    ...detail,
    attachments: detail.attachments ?? [],
    comments: (detail.comments ?? []).map((comment) => ({
      ...comment,
      attachments: comment.attachments ?? [],
    })),
  }
}

export type FinanceProjectRow = {
  id: string
  name: string | null
  status: string
  source: string
  createdAt: string
  empresa: { id: string; nome: string; cnpj: string | null; status: string }
  deal?: { id: string; nome: string; etapa: string } | null
  requests: { id: string; protocol: string; status: string }[]
}

export type FinanceContractRow = {
  id: string
  type: string
  fileUrl: string
  version: number
  status: string
  createdAt: string
  uploadedBy?: { id: string; name: string } | null
}

export type FinanceProposalSnapshotRow = {
  id: string
  versao: number
  totalFinal: number | string | null
  pdfUrl: string | null
  createdAt: string
  propostaId?: string | null
  payloadJson?: {
    items?: unknown[]
    dados?: string | Record<string, unknown>
    link?: string | null
  } | null
}

export type FinanceProjectDetail = FinanceProjectRow & {
  contracts: FinanceContractRow[]
  proposalSnapshots: FinanceProposalSnapshotRow[]
  requests: { id: string; protocol: string; type: string; status: string; createdAt: string }[]
}

export const FINANCE_REQUEST_STATUSES = [
  'NOVO',
  'EM_ANALISE',
  'EM_ANDAMENTO',
  'AGUARDANDO_SOLICITANTE',
  'RESPONDIDO_SOLICITANTE',
  'AGUARDANDO_APROVACAO',
  'CONTRATO_GERADO_PDF',
  'CONTRATO_ENVIADO',
  'CONTRATO_ASSINADO',
  'CONCLUIDO',
  'CANCELADO',
  'PAUSADO',
] as const

export const financeService = {
  async getSummary() {
    const { data } = await api.get<{ data: FinanceSummary }>('/finance/summary')
    return data.data
  },

  async listRequests(params?: {
    page?: number
    limit?: number
    queueLifecycle?: 'ativos' | 'pausados' | 'cancelados' | 'historico'
    status?: string
    type?: string
    area?: string
    assigneeId?: string
    unassigned?: boolean
    slaFilter?: 'overdue' | 'due_soon' | 'ok'
    search?: string
  }) {
    const { data } = await api.get<{ data: FinanceRequestRow[]; meta: { totalItems: number } }>(
      '/finance/requests',
      { params },
    )
    return data
  },

  async getRequest(id: string) {
    const { data } = await api.get<{ data: FinanceRequestDetail }>(`/finance/requests/${id}`)
    return normalizeFinanceRequestDetail(data.data)
  },

  async createG5PjInvite(requestId: string) {
    const { data } = await api.post<{ data: FinanceRequestDetail }>(
      `/finance/requests/${requestId}/pj-invite`,
    )
    return normalizeFinanceRequestDetail(data.data)
  },

  async updateRequestStatus(id: string, status: string, approvalPendingFrom?: string) {
    const { data } = await api.patch<{ data: FinanceRequestDetail }>(`/finance/requests/${id}/status`, {
      status,
      approvalPendingFrom,
    })
    return data.data
  },

  async assignRequest(id: string, assigneeId: string | null) {
    const { data } = await api.patch<{ data: FinanceRequestDetail }>(`/finance/requests/${id}/assignee`, {
      assigneeId,
    })
    return data.data
  },

  async listAssignees() {
    const { data } = await api.get<{ data: FinanceAssignee[] }>('/finance/assignees')
    return data.data
  },

  async exportRequestsCsv(): Promise<Blob> {
    const { data } = await api.get<Blob>('/finance/requests/export', { responseType: 'blob' })
    return data
  },

  async prefillEmpresa(empresaId: string) {
    const { data } = await api.get<{
      data: {
        empresa: { id: string; nome: string; cnpj: string | null }
        prefill: Record<string, string>
        deals: { id: string; nome: string; etapa: string; valor: number | null }[]
      }
    }>(`/finance/prefill/empresa/${empresaId}`)
    return data.data
  },

  async prefillDeal(dealId: string) {
    const { data } = await api.get<{
      data: {
        linkedEmpresaId: string
        linkedDealId: string
        prefill: Record<string, string>
      }
    }>(`/finance/prefill/deal/${dealId}`)
    return data.data
  },

  async getContractRequestForDeal(dealId: string) {
    const { data } = await api.get<{ data: FinanceRequestDetail | null }>(
      `/finance/deals/${dealId}/contract-request`,
    )
    const raw = data.data ?? null
    return raw ? normalizeFinanceRequestDetail(raw) : null
  },

  async addRequestComment(id: string, body: string, isInternal = false, attachments: string[] = []) {
    const { data } = await api.post<{
      data: FinanceRequestComment
      requestStatus?: string
    }>(`/finance/requests/${id}/comments`, { body, isInternal, attachments })
    return { comment: data.data, requestStatus: data.requestStatus }
  },

  async fixPropostaForDeal(dealId: string) {
    const { data } = await api.post<{
      data: { projectId: string; snapshotId: string }
    }>(`/finance/deals/${dealId}/fix-proposta`)
    return data.data
  },

  async listProjects(params?: { page?: number; limit?: number; status?: string; search?: string; projectFilter?: string }) {
    const { data } = await api.get<{ data: FinanceProjectRow[]; meta: { totalItems: number } }>(
      '/finance/projects',
      { params },
    )
    return data
  },

  async getProject(id: string) {
    const { data } = await api.get<{ data: FinanceProjectDetail }>(`/finance/projects/${id}`)
    return data.data
  },

  async uploadContract(projectId: string, file: File, type = 'comercial') {
    const form = new FormData()
    form.append('file', file)
    form.append('type', type)
    const { data } = await api.post<{ data: FinanceContractRow }>(
      `/finance/projects/${projectId}/contracts`,
      form,
    )
    return data.data
  },

  async updateC4Deliverables(id: string, body: { contractSignatureLink?: string }) {
    const { data } = await api.patch<{ data: FinanceRequestDetail }>(
      `/finance/requests/${id}/c4-deliverables`,
      body,
    )
    return data.data
  },

  async listTypes() {
    const { data } = await api.get<{
      data: {
        key: string
        label: string
        area: string
        slaHours: number
        mvp?: boolean
        portal?: boolean
        c4ManualOnly?: boolean
      }[]
    }>('/finance/types')
    return data.data
  },

  async uploadRequestAttachment(file: File) {
    const form = new FormData()
    form.append('file', file)
    const { data } = await api.post<{
      data: { fileUrl: string; fileName: string; mimeType: string }
    }>('/finance/requests/attachments', form)
    return data.data
  },

  async createRequest(body: {
    type: string
    linkedEmpresaId?: string
    linkedDealId?: string
    formData?: Record<string, unknown>
    attachments?: string[]
  }) {
    const { data } = await api.post<{
      data: { id: string; protocol: string; type: string; status: string }
    }>('/finance/requests', body)
    return data.data
  },
}
